import React, { useState, useEffect } from 'react';
import { X, Search, Building2 } from 'lucide-react';
import { CompanyResource } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface CompanySearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (company: CompanyResource) => void;
  title?: string;
}

const CompanySearchDialog: React.FC<CompanySearchDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  title = 'Select Company'
}) => {
  const [companies, setCompanies] = useState<CompanyResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load companies when search changes
  useEffect(() => {
    if (search) {
      loadCompanies();
    } else {
      setCompanies([]);
    }
  }, [search]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.getCompanies(1, 10, search);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error loading companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (company: CompanyResource) => {
    onSelect(company);
    onClose();
  };

  const handleClose = () => {
    setSearchInput('');
    setSearch('');
    setCompanies([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Search Input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>

          {/* Companies List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" text="Searching companies..." />
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
                <p className="text-gray-500">
                  {search ? 'Try adjusting your search terms' : 'Start typing to search for companies'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {companies.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => handleSelect(company)}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 mr-3">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {company.companyName}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {company.brandName}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {company.companyCode} • {company.businessType}
                        </div>
                        {company.companyAdminEmail && (
                          <div className="text-xs text-gray-400">
                            {company.companyAdminEmail}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySearchDialog;
